import React, { useState, useMemo } from "react";
import { Card, Table, Input, TreeSelect, Tag, Tooltip, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { concat } from "lodash";
import { findCategoryBySlug, getProductsByCategory } from "../../../../functions/categories";
import { usePurchaseOrders, usePurchaseOrderShipments } from "../../../../data/hooks";
import AvatarStatus from "../../../../components/shared-components/AvatarStatus";
import urls from "../../../../urls";
import utils from "../../../../utils";
import { getProductStockStatus } from "../../../../utils/stockCalculations";
import DiscontinuedMark from "../../../../components/shared-components/DiscontinuedMark";

const StockOverview = () => {
  const { t } = useTranslation();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");

  // Fetch purchase orders and shipments
  const { purchaseOrders } = usePurchaseOrders();
  const { shipments } = usePurchaseOrderShipments();

  // Calculate stock data for each product
  const stockData = useMemo(() => {
    if (!products || !purchaseOrders || !shipments) {
      console.log("⚠️ Missing data:", {
        products: !!products,
        purchaseOrders: !!purchaseOrders,
        shipments: !!shipments,
      });
      return [];
    }

    return products.map((product) => {
      const stockStatus = getProductStockStatus(product, purchaseOrders, shipments);
      
      return {
        ...product,
        currentStock: stockStatus.currentStock,
        orderedQuantity: stockStatus.openOrders,
        inTransitQuantity: stockStatus.inTransit,
        receivedQuantity: stockStatus.received,
        expectedStock: stockStatus.expectedTotal,
        stockDetails: stockStatus,
      };
    });
  }, [products, purchaseOrders, shipments]);

  // Apply filters
  const filteredData = useMemo(() => {
    let data = stockData;

    // Category filter
    if (categoryFilter !== "all") {
      const category = findCategoryBySlug(categories, categoryFilter);
      data = getProductsByCategory(data, category);
    }

    // Search filter
    if (searchText) {
      data = utils.wildCardSearchProductName(data, searchText);
    }

    // Stock level filter
    if (stockFilter === "low") {
      data = data.filter((p) => p.currentStock < 5);
    } else if (stockFilter === "zero") {
      data = data.filter((p) => p.currentStock === 0);
    } else if (stockFilter === "in_transit") {
      data = data.filter((p) => p.inTransitQuantity > 0);
    } else if (stockFilter === "ordered") {
      data = data.filter((p) => p.orderedQuantity > 0);
    } else if (stockFilter === "expected") {
      data = data.filter((p) => p.orderedQuantity > 0 || p.inTransitQuantity > 0);
    }

    return data;
  }, [stockData, categoryFilter, searchText, stockFilter, categories]);

  // Get stock color based on level
  const getStockColor = (stock) => {
    if (stock === 0) return "#ff4d4f"; // red
    if (stock < 5) return "#faad14"; // orange
    return "#52c41a"; // green
  };

  const columns = [
    {
      title: t("product"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div className="d-flex">
          {/* Uitlopend-stip op de foto (BMX-49) -- een tag naast een foto van 60px laat de rij
              verspringen. */}
          <DiscontinuedMark discontinued={record.discontinued} variant="badge">
            <AvatarStatus
              size={60}
              type="square"
              src={!!record.image ? record.image[0] : null}
              name={record.name}
              linkTo={urls.editProduct + record.id}
            />
          </DiscontinuedMark>
        </div>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
      render: (_, product) => <span>{product.code + (product.barcode ? " / " + product.barcode : "")}</span>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "code"),
    },
    {
      title: t("current_stock"),
      dataIndex: "currentStock",
      key: "currentStock",
      render: (stock) => <span style={{ color: getStockColor(stock), fontWeight: "bold" }}>{stock}</span>,
      sorter: (a, b) => a.currentStock - b.currentStock,
    },
    {
      title: t("ordered"),
      dataIndex: "orderedQuantity",
      key: "orderedQuantity",
      render: (qty, record) => {
        if (qty === 0) return <span style={{ color: "#999" }}>-</span>;

        return (
          <Tooltip
            title={
              <div>
                {record.stockDetails.inOrders.orders.map((order, idx) => (
                  <div key={idx}>
                    {order.orderNumber}: {order.amount}x
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="blue" style={{ cursor: "pointer" }}>
              {qty}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.orderedQuantity - b.orderedQuantity,
    },
    {
      title: t("in_transit"),
      dataIndex: "inTransitQuantity",
      key: "inTransitQuantity",
      render: (qty, record) => {
        if (qty === 0) return <span style={{ color: "#999" }}>-</span>;

        return (
          <Tooltip
            title={
              <div>
                {record.stockDetails.inTransitShipments.map((shipment, idx) => (
                  <div key={idx}>
                    {shipment.shipmentNumber}: {shipment.amount}x
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="orange" style={{ cursor: "pointer" }}>
              {qty}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.inTransitQuantity - b.inTransitQuantity,
    },
    {
      title: t("expected_stock"),
      dataIndex: "expectedStock",
      key: "expectedStock",
      render: (stock, record) => (
        <Tooltip
          title={
            <div>
              <div>
                {t("current_stock")}: {record.currentStock}
              </div>
              <div>
                {t("ordered")}: {record.orderedQuantity}
              </div>
              <div>
                {t("in_transit")}: {record.inTransitQuantity}
              </div>
              <div style={{ marginTop: 4, borderTop: "1px solid #fff", paddingTop: 4 }}>
                <strong>
                  {t("total")}: {stock}
                </strong>
              </div>
            </div>
          }
        >
          <span style={{ fontWeight: "bold", color: "#1890ff" }}>{stock}</span>
        </Tooltip>
      ),
      sorter: (a, b) => a.expectedStock - b.expectedStock,
    },
  ];

  return (
    <>
      <h2>{t("stock_overview")}</h2>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <TreeSelect
              showSearch
              onChange={setCategoryFilter}
              style={{ width: 200 }}
              placeholder={t("form.choose_category")}
              allowClear
              treeDefaultExpandAll
              value={categoryFilter}
              treeData={concat([{ slug: "all" }], categories || []).map((category) => ({
                key: category?.slug,
                title: category?.slug === "all" ? t("all_categories") : category?.name,
                value: category?.slug,
                children:
                  !!category?.subcategories &&
                  category.subcategories.map((subcategory) => ({
                    title: subcategory.name,
                    value: subcategory.slug,
                    key: subcategory.slug,
                  })),
              }))}
            />
            <TreeSelect
              onChange={setStockFilter}
              style={{ width: 200 }}
              placeholder={t("stock_filter")}
              value={stockFilter}
              treeData={[
                { title: t("all_products"), value: "all" },
                { title: t("stock_zero"), value: "zero" },
                { title: t("stock_low"), value: "low" },
                { title: t("with_orders"), value: "ordered" },
                { title: t("in_transit"), value: "in_transit" },
                { title: "Met verwachte voorraad", value: "expected" },
              ]}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{ pageSize: 50 }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </>
  );
};

export default StockOverview;
